import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileText, Edit, Pencil, Trash2, Download } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Table, Column } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { ConfirmDeleteModal } from '../../components/ui/ConfirmDeleteModal';
import { PropertyForm } from '../../components/forms/PropertyForm';
import { Investment, Tenant, Unit, PropertyDocument, Property } from '../../types';
import { validatePropertyOwnership } from '../../utils/validation';
import { formatDate, formatMonthYear, todayLocalDateString } from '../../utils/date';
import { buildCsv, downloadCsvFile } from '../../utils/exportCsv';
import {
  api,
  getPropertyCoverImageUrl,
  getPropertyDocumentDownloadUrl,
  PROPERTY_IMAGE_PLACEHOLDER,
} from '../../services/api';
import { DocumentViewer } from '../../components/documents/DocumentViewer';
import { usePermissions } from '../../hooks/usePermissions';
import { HistoryTable } from '../../components/history/HistoryTable';
import { mapTimelineToChangeLogEntries, mergeHistoryRows } from '../../utils/historyRows';
import {
  computePlusvaliaPct,
  formatPlusvaliaPct,
  formatPropertyMoney,
} from '../../utils/propertyValue';

function toDateInputValue(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'string') {
    // Backend suele mandar ISO (2026-04-30T00:00:00.000Z); input date requiere YYYY-MM-DD
    if (v.includes('T')) return v.split('T')[0] || '';
    return v;
  }
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return v.toISOString().split('T')[0] || '';
  }
  return '';
}

const ACCOUNT_STATEMENT_ALLOWED_EXTENSIONS = ['pdf', 'xlsx', 'xls', 'csv'];
const ACCOUNT_STATEMENT_ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
  'application/csv',
];

function slugifyFilenamePart(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function moneyForCsv(value: number | null | undefined, canViewPrices: boolean): string {
  if (!canViewPrices) return 'Sin permiso para ver montos';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export const PropertyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<
    'general' | 'investors' | 'tenants' | 'units' | 'documents' | 'history' | 'statements'
  >('general');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [property, setProperty] = useState<Property | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editingUnit, setEditingUnit] = useState<Unit | null>(null);
  const [unitForm, setUnitForm] = useState<{ tenantId: string; monthlyRent: string; contractStartDate: string; contractEndDate: string; status: Unit['status'] }>({ tenantId: '', monthlyRent: '', contractStartDate: '', contractEndDate: '', status: 'available' });
  const [unitSaveLoading, setUnitSaveLoading] = useState(false);
  const [isPropertyFormSubmitting, setIsPropertyFormSubmitting] = useState(false);

  const { hasPermission, canView, canUpdate } = usePermissions();
  const canViewPrices = hasPermission('view_prices');
  const canViewUnits = canView('units');
  const canUpdateUnits = canUpdate('units');
  const canViewInvestors = canView('investors');
  const canUpdateProperties = canUpdate('properties');
  const canDeleteProperties = hasPermission('delete_properties');
  const canManageAccountStatements =
    hasPermission('manage_investors') || hasPermission('update_investors');

  const [statementModalOpen, setStatementModalOpen] = useState(false);
  const [statementSubmitting, setStatementSubmitting] = useState(false);
  const [statementError, setStatementError] = useState<string | null>(null);
  const [statementFile, setStatementFile] = useState<File | null>(null);
  const [statementForm, setStatementForm] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    income: '',
    expenses: '',
    netIncome: '',
    propertyProfit: '',
  });
  /** Edición de un registro existente (tabla property_account_statements). */
  const [propertyStatementEdit, setPropertyStatementEdit] = useState<{
    id: string;
    fileUrl?: string | null;
  } | null>(null);
  const [pendingDeleteStatement, setPendingDeleteStatement] = useState<{
    id: string;
    periodLabel: string;
  } | null>(null);
  const [pendingDeleteDocument, setPendingDeleteDocument] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const deletePropertyDocumentMutation = useMutation({
    mutationFn: (documentId: string) => api.deletePropertyDocument(documentId),
    onSuccess: async (_data, documentId) => {
      await queryClient.invalidateQueries({ queryKey: ['property', id] });
      setPendingDeleteDocument(null);
      if (property) {
        setProperty({
          ...property,
          documents: (property.documents ?? []).filter((d) => d.id !== documentId),
        });
      }
    },
  });

  const deletePropertyStatementMutation = useMutation({
    mutationFn: (statementId: string) =>
      api.deletePropertyAccountStatementPeriod(statementId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['property-account-statements', id] });
      await queryClient.invalidateQueries({ queryKey: ['account-statements'] });
      setPendingDeleteStatement(null);
    },
  });

  const closeStatementModal = () => {
    setStatementModalOpen(false);
    setStatementError(null);
    setStatementFile(null);
    setPropertyStatementEdit(null);
  };

  const handleStatementFileChange = (file: File | null) => {
    if (!file) {
      setStatementFile(null);
      return;
    }
    const ext = file.name.includes('.')
      ? file.name.split('.').pop()?.toLowerCase() || ''
      : '';
    const mime = (file.type || '').toLowerCase();
    const extAllowed = ACCOUNT_STATEMENT_ALLOWED_EXTENSIONS.includes(ext);
    const mimeAllowed = ACCOUNT_STATEMENT_ALLOWED_MIME_TYPES.includes(mime);

    if (!extAllowed || !mimeAllowed) {
      setStatementFile(null);
      setStatementError(
        'Archivo no válido. Solo se permiten PDF, Excel (.xlsx, .xls) o CSV.',
      );
      return;
    }
    setStatementError(null);
    setStatementFile(file);
  };

  const resetStatementFormDefaults = () => {
    setStatementForm({
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      income: '',
      expenses: '',
      netIncome: '',
      propertyProfit: '',
    });
  };

  const openCreateStatementModal = () => {
    setStatementError(null);
    setStatementFile(null);
    setPropertyStatementEdit(null);
    resetStatementFormDefaults();
    setStatementModalOpen(true);
  };

  const openEditStatementModal = (s: {
    id: string;
    month: number;
    year: number;
    period?: string;
    income: number;
    expenses: number;
    netIncome: number;
    propertyProfit: number;
    fileUrl?: string | null;
  }) => {
    setStatementError(null);
    setStatementFile(null);
    setPropertyStatementEdit({ id: s.id, fileUrl: s.fileUrl });
    setStatementForm({
      month: s.month,
      year: s.year,
      income: Number.isFinite(s.income) ? String(s.income) : '',
      expenses: Number.isFinite(s.expenses) ? String(s.expenses) : '',
      netIncome: Number.isFinite(s.netIncome) ? String(s.netIncome) : '',
      propertyProfit: Number.isFinite(s.propertyProfit) ? String(s.propertyProfit) : '',
    });
    setStatementModalOpen(true);
  };

  const { data: propertyStatementsRaw, isLoading: propertyStatementsLoading } = useQuery({
    queryKey: ['property-account-statements', id],
    queryFn: () => api.getPropertyAccountStatements(id!),
    enabled: !!id && activeTab === 'statements' && canManageAccountStatements,
    staleTime: 30_000,
  });

  const propertyStatements = useMemo(() => {
    const rows = Array.isArray(propertyStatementsRaw) ? propertyStatementsRaw : [];
    return rows.map((r: any) => ({
      id: r.id,
      month: Number(r.month),
      year: Number(r.year),
      period: r.period,
      income: Number(r.income ?? 0),
      expenses: Number(r.expenses ?? 0),
      netIncome: Number(r.netIncome ?? 0),
      propertyProfit: Number(r.propertyProfit ?? r.property_profit ?? 0),
      fileUrl: r.fileUrl ?? r.file_url,
    }));
  }, [propertyStatementsRaw]);

  const { data: propertyTimeline = [], isLoading: propertyTimelineLoading } = useQuery({
    queryKey: ['property-timeline', id],
    queryFn: () => api.getPropertyTimeline(id!),
    enabled: !!id && activeTab === 'history',
    staleTime: 30_000,
  });
  const { data: changeLogsPersisted = [], isLoading: changeLogsLoading } = useQuery({
    queryKey: ['change-logs', 'PROPERTY', id],
    queryFn: () => api.getChangeLogs({ entityId: id!, entityType: 'PROPERTY', limit: 200 }),
    enabled: !!id && activeTab === 'history',
    staleTime: 30_000,
  });

  const {
    data: propertyData,
    isLoading: propertyQueryLoading,
    error: propertyQueryError,
  } = useQuery({
    queryKey: ['property', id],
    queryFn: () => api.getProperty(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
  const loading = propertyQueryLoading;

  const historyRows = useMemo(() => {
    if (!id) return [];
    return mergeHistoryRows(
      mapTimelineToChangeLogEntries(id, 'PROPERTY', propertyTimeline),
      changeLogsPersisted,
    );
  }, [id, propertyTimeline, changeLogsPersisted]);
  const historyLoading = propertyTimelineLoading || changeLogsLoading;

  // Función helper para transformar propiedad del backend al formato del frontend
  const transformProperty = (propertyData: any): Property => {
    // Transformar documentos del backend al formato del frontend
    const documents = (propertyData.documents || propertyData.property_documents || []).map((doc: any) => ({
      id: doc.id,
      title: doc.title,
      fileUrl: doc.fileUrl,
      fileType: doc.fileType,
      uploadedAt: doc.uploadedAt || doc.createdAt || new Date().toISOString(),
    }));
    
    return {
      id: propertyData.id,
      name: propertyData.name,
      address: propertyData.address,
      city: propertyData.city ?? '',
      state: propertyData.state ?? '',
      unitCount: propertyData.unitCount || 0,
      occupiedUnits: propertyData.occupiedUnits || propertyData.tenants?.length || 0,
      m2Total: propertyData.m2Total || 0,
      pricePerM2: propertyData.pricePerM2 || 0,
      totalValue: propertyData.totalValue || 0,
      currentValue:
        propertyData.currentValue != null
          ? Number(propertyData.currentValue)
          : null,
      status: propertyData.status || 'en-proyecto',
      coverImage: propertyData.coverImage,
      documents,
      createdAt: propertyData.createdAt || new Date().toISOString(),
    };
  };

  useEffect(() => {
    if (!propertyData) {
      if (!propertyQueryLoading) {
        setProperty(null);
        setUnits([]);
        setInvestments([]);
        setTenants([]);
      }
      return;
    }

    setProperty(transformProperty(propertyData));

    const unitsData: any[] = Array.isArray(propertyData.units) ? propertyData.units : [];
    const tenantsForUnits = propertyData.tenants || [];
    setUnits(
      unitsData.map((unit: any) => ({
        id: unit.id,
        propertyId: unit.propertyId,
        unitNumber: unit.unitNumber,
        m2: typeof unit.m2 === 'number' ? unit.m2 : parseFloat(unit.m2) || 0,
        bedrooms: unit.bedrooms,
        bathrooms: unit.bathrooms,
        status: unit.status || 'available',
        tenantId: unit.tenantId,
        tenantName:
          unit.tenant?.name ||
          unit.tenants?.name ||
          tenantsForUnits.find((t: any) => t.id === unit.tenantId)?.name ||
          unit.tenantName,
        monthlyRent: unit.monthlyRent
          ? typeof unit.monthlyRent === 'number'
            ? unit.monthlyRent
            : parseFloat(unit.monthlyRent)
          : undefined,
        contractStartDate: unit.contractStartDate,
        contractEndDate: unit.contractEndDate,
        notes: unit.notes,
      })),
    );

    setInvestments(
      (propertyData.investments || []).map((inv: any) => ({
        id: inv.id,
        investorId: inv.investorId,
        investorName: inv.investorName || inv.investors?.name || '',
        propertyId: inv.propertyId,
        propertyName: inv.propertyName || inv.properties?.name || propertyData.name || '',
        amount: typeof inv.amount === 'number' ? inv.amount : parseFloat(String(inv.amount)) || 0,
        ownershipPct:
          typeof inv.ownershipPct === 'number'
            ? inv.ownershipPct
            : parseFloat(String(inv.ownershipPct)) || 0,
        status: inv.status || 'active',
        startDate: inv.startDate,
        endDate: inv.endDate,
        notes: inv.notes,
        createdAt: inv.createdAt,
      })),
    );

    const tenantsData: any[] = Array.isArray(propertyData.tenants) ? propertyData.tenants : [];
    setTenants(
      tenantsData.map((t: any) => {
        const startDate = t.startDate
          ? typeof t.startDate === 'string'
            ? t.startDate
            : new Date(t.startDate).toISOString().split('T')[0]
          : '';
        const endDate = t.endDate
          ? typeof t.endDate === 'string'
            ? t.endDate
            : new Date(t.endDate).toISOString().split('T')[0]
          : '';
        return {
          id: t.id,
          name: t.name,
          email: t.email,
          phone: t.phone,
          propertyId: t.propertyId,
          unitNumber: t.unitNumber,
          monthlyRent: Number(t.monthlyRent) || 0,
          startDate,
          endDate,
          status: t.status || 'active',
        };
      }),
    );
  }, [propertyData, propertyQueryLoading]);

  useEffect(() => {
    if (propertyQueryError) {
      setError(
        propertyQueryError instanceof Error
          ? propertyQueryError.message
          : 'Error al cargar la propiedad',
      );
    } else if (propertyData) {
      setError(null);
    }
  }, [propertyQueryError, propertyData]);

  const reloadUnitsAndTenants = useCallback(async () => {
    if (!id) return;
    try {
      await queryClient.invalidateQueries({ queryKey: ['property', id] });
    } catch (e) {
      console.error('Error reloading units/tenants:', e);
    }
  }, [id, queryClient]);
  
  // Filtrar inversiones activas de esta propiedad
  const propertyInvestments = investments.filter(
    (inv: Investment) => inv.propertyId === id && inv.status === 'active'
  );
  const propertyTenants = tenants.filter((t) => t.propertyId === id);
  // Unidades enriquecidas con datos del inquilino; ocupada si hay inquilino activo (si no, disponible)
  const propertyUnits = useMemo<Unit[]>(() => {
    const enriched: Unit[] = units.map((unit) => {
      const tenantForUnit = tenants.find(
        (t) => t.propertyId === id && String(t.unitNumber) === String(unit.unitNumber),
      );

      const hasActiveTenant = !!unit.tenantId || (tenantForUnit?.status === 'active');
      const derivedStatus: Unit['status'] = hasActiveTenant ? 'occupied' : 'available';

      return {
        ...unit,
        status: derivedStatus,
        tenantId: unit.tenantId || tenantForUnit?.id,
        tenantName: unit.tenantName || tenantForUnit?.name || undefined,
        monthlyRent: unit.monthlyRent ?? tenantForUnit?.monthlyRent,
        contractStartDate: unit.contractStartDate || tenantForUnit?.startDate,
        contractEndDate: unit.contractEndDate || tenantForUnit?.endDate,
      };
    });

    return enriched.sort((a, b) => {
      const numA = Number(a.unitNumber);
      const numB = Number(b.unitNumber);
      if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB;
      return String(a.unitNumber).localeCompare(String(b.unitNumber), undefined, { numeric: true });
    });
  }, [units, tenants, id]);

  const ownershipTotal = useMemo(() => {
    return propertyInvestments.reduce((sum: number, inv: Investment) => sum + inv.ownershipPct, 0);
  }, [propertyInvestments]);

  const ownershipValidation = useMemo(() => {
    return validatePropertyOwnership(investments, id || '', 0);
  }, [investments, id]);

  // Si la pestaña activa ya no está permitida (ej. sin permiso de unidades o inversionistas), volver a General
  useEffect(() => {
    if (activeTab === 'units' && !canViewUnits) setActiveTab('general');
    if (activeTab === 'investors' && !canViewInvestors) setActiveTab('general');
  }, [activeTab, canViewUnits, canViewInvestors]);

  // Mostrar loading
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-600">Cargando propiedad...</p>
        </div>
      </div>
    );
  }

  // Mostrar error o propiedad no encontrada
  if (error || !property) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{error || 'Propiedad no encontrada'}</p>
        <Button onClick={() => navigate('/propiedades')} className="mt-4">
          Volver a la lista
        </Button>
      </div>
    );
  }

  const investmentColumns: Column<Investment>[] = [
    { header: 'Inversionista', accessor: 'investorName' },
    {
      header: 'Monto',
      accessor: (row) => (canViewPrices ? `$${row.amount.toLocaleString()}` : '—'),
    },
    {
      header: 'Participación',
      accessor: (row) => `${row.ownershipPct}%`,
    },
    {
      header: 'Fecha Inicio',
      accessor: (row) => formatDate(row.startDate),
    },
  ];

  const tenantColumns: Column<Tenant>[] = [
    { header: 'Nombre', accessor: 'name' },
    { header: 'Email', accessor: 'email' },
    { header: 'Unidad', accessor: 'unitNumber' },
    {
      header: 'Renta Mensual',
      accessor: (row) => (canViewPrices ? `$${row.monthlyRent.toLocaleString()}` : '—'),
    },
    {
      header: 'Estado',
      accessor: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            row.status === 'active'
              ? 'bg-green-100 text-green-800'
              : row.status === 'pending'
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {row.status === 'active' ? 'Activo' : row.status === 'pending' ? 'Pendiente' : 'Expirado'}
        </span>
      ),
    },
  ];

  const unitColumns: Column<Unit>[] = [
    { header: 'Unidad', accessor: 'unitNumber', sortable: true },
    {
      header: 'm²',
      accessor: (row) => `${row.m2} m²`,
      sortable: true,
    },
    {
      header: 'Habitaciones',
      accessor: (row) => row.bedrooms || '-',
      sortable: true,
    },
    {
      header: 'Baños',
      accessor: (row) => row.bathrooms || '-',
      sortable: true,
    },
    {
      header: 'Estado',
      accessor: (row) => {
        const statusConfig = {
          available: { label: 'Disponible', class: 'bg-blue-100 text-blue-800' },
          occupied: { label: 'Ocupada', class: 'bg-green-100 text-green-800' },
          maintenance: { label: 'Mantenimiento', class: 'bg-yellow-100 text-yellow-800' },
          reserved: { label: 'Reservada', class: 'bg-purple-100 text-purple-800' },
        };
        const config = statusConfig[row.status] || statusConfig.available;
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.class}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      header: 'Inquilino',
      accessor: (row) => row.tenantName || '-',
    },
    {
      header: 'Renta Mensual',
      accessor: (row) => (canViewPrices && row.monthlyRent ? `$${row.monthlyRent.toLocaleString()}` : canViewPrices ? '-' : '—'),
    },
    {
      header: 'Contrato',
      accessor: (row) => {
        if (row.contractStartDate && row.contractEndDate) {
          const start = formatMonthYear(row.contractStartDate);
          const end = formatMonthYear(row.contractEndDate);
          return `${start} - ${end}`;
        }
        return '-';
      },
    },
    ...(canUpdateUnits
      ? [
          {
            header: 'Acciones',
            accessor: (row: Unit) => (
              <button
                type="button"
                onClick={() => {
                  setEditingUnit(row);
                  setUnitForm({
                    tenantId: row.tenantId || '',
                    monthlyRent: row.monthlyRent != null ? String(row.monthlyRent) : '',
                    contractStartDate: toDateInputValue(row.contractStartDate),
                    contractEndDate: toDateInputValue(row.contractEndDate),
                    status: row.status,
                  });
                }}
                className="inline-flex items-center justify-center p-2 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors"
                title="Editar unidad / Asignar inquilino"
                aria-label="Editar unidad o asignar inquilino"
              >
                <Pencil className="w-4 h-4" />
              </button>
            ),
          } as Column<Unit>,
        ]
      : []),
  ];

  const handleOpenEditModal = () => {
    // Abrir modal al instante con los datos actuales (la propiedad ya está cargada en la página de detalle)
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleDownloadBackOfficeReport = () => {
    if (!property) return;

    const headers = [
      'Sección',
      'Campo / Nombre',
      'Monto',
      'Participación',
      'Tipo / Estado',
      'Fecha / Valor',
    ];

    const rows: string[][] = [
      ['Resumen', 'Propiedad', '', '', '', property.name],
      ['Resumen', 'Dirección', '', '', '', property.address],
      ['Resumen', 'Ciudad', '', '', '', property.city],
      ['Resumen', 'Estado', '', '', '', property.state || ''],
      ['Resumen', 'Estatus operativo', '', '', '', property.status],
      ['Resumen', 'Unidades', '', '', '', `${property.occupiedUnits} ocupadas de ${property.unitCount}`],
      ['Resumen', 'Metros cuadrados', '', '', '', `${property.m2Total} m2`],
      ['Resumen', 'Precio por m2', moneyForCsv(property.pricePerM2, canViewPrices), '', '', ''],
      ['Resumen', 'Costo total asignado', moneyForCsv(property.totalValue, canViewPrices), '', '', ''],
      ['Resumen', 'Valor actual', moneyForCsv(property.currentValue, canViewPrices), '', '', ''],
      ['Resumen', 'Participación total asignada', '', `${ownershipTotal.toFixed(2)}%`, '', ''],
      ['', '', '', '', '', ''],
      ...propertyInvestments.map((investment) => [
        'Socios',
        investment.investorName || investment.investorId,
        moneyForCsv(investment.amount, canViewPrices),
        `${Number(investment.ownershipPct || 0).toFixed(2)}%`,
        investment.status,
        investment.startDate ? formatDate(investment.startDate) : '',
      ]),
      ['', '', '', '', '', ''],
      ...((property.documents ?? []).length > 0
        ? (property.documents ?? []).map((document) => [
            'Documentos',
            document.title,
            '',
            '',
            document.fileType || '',
            document.uploadedAt ? formatDate(document.uploadedAt) : '',
          ])
        : [['Documentos', 'Sin documentos cargados', '', '', '', '']]),
    ];

    const filenameBase = slugifyFilenamePart(property.name) || 'propiedad';
    downloadCsvFile(
      `reporte-propiedad-${filenameBase}-${todayLocalDateString()}.csv`,
      buildCsv(headers, rows),
    );
  };

  const handleEditSubmit = async (propertyData: Omit<Property, 'id' | 'createdAt'>, units?: Omit<Unit, 'id' | 'propertyId'>[]) => {
    if (!property || !id) return;
    
    try {
      setError(null);
      setIsPropertyFormSubmitting(true);
      
      // Preparar datos para enviar (sin documentos, estado y occupiedUnits)
      const { documents, estado, occupiedUnits, ...restData } = propertyData as any;
      
      // Eliminar campos que no están en los DTOs del backend
      const propertyPayload: any = { ...restData };
      delete propertyPayload.estado;
      delete propertyPayload.occupiedUnits;
      // coverImage va en el payload (base64 o ruta existente)
      
      // Actualizar propiedad en el backend
      const updatedProperty = await api.updateProperty(id, propertyPayload);
      
      // Guardar/actualizar documentos
      if (documents && documents.length > 0) {
        try {
          // Obtener documentos existentes
          const existingProperty = await api.getProperty(id);
          const existingDocuments = existingProperty.documents || existingProperty.property_documents || [];
          const existingDocsMap = new Map(existingDocuments.map((d: any) => [d.id, d]));
          
          // Procesar cada documento del formulario
          for (const doc of documents) {
            // Si el documento tiene un ID real (UUID) y existe en la base de datos, ya existe
            // Los IDs temporales (que empiezan con "doc-") deben crearse siempre
            const isTemporaryId = doc.id && doc.id.toString().startsWith('doc-');
            if (doc.id && !isTemporaryId && existingDocsMap.has(doc.id)) {
              // El documento ya existe, no hacer nada
              continue;
            } else {
              // Crear nuevo documento (ya sea porque no tiene ID, tiene ID temporal, o no existe en la BD)
              await api.addPropertyDocument(id, {
                title: doc.title,
                fileUrl: doc.fileUrl,
                fileType: doc.fileType,
              });
            }
          }
          
          // Eliminar documentos que ya no están en el formulario
          const formDocIds = new Set(documents.filter((d: any) => d.id).map((d: any) => d.id));
          for (const existingDoc of existingDocuments) {
            if (!formDocIds.has(existingDoc.id)) {
              try {
                await api.deletePropertyDocument(existingDoc.id);
              } catch (deleteErr) {
                console.error('Error deleting document:', deleteErr);
              }
            }
          }
        } catch (docsErr) {
          console.error('Error saving documents:', docsErr);
          // No fallar todo si solo falla el guardado de documentos
        }
      }
      
      // Recargar la propiedad completa para obtener documentos actualizados
      const fullProperty = await api.getProperty(id);
      
      // Actualizar el estado local
      setProperty(transformProperty(fullProperty));
      
      // Guardar unidades en el backend (crear/actualizar las del formulario y eliminar las que ya no están)
      if (units !== undefined) {
        try {
          const unitsRaw = await api.getUnits(id);
          const existingUnitsList: any[] = Array.isArray(unitsRaw) ? unitsRaw : (unitsRaw as any)?.data ?? [];

          if (units.length > 0) {
            const existingUnitsMap = new Map(existingUnitsList.map((u: any) => [u.unitNumber, u]));

            for (const unit of units) {
              const existingUnit = existingUnitsMap.get(unit.unitNumber) as { id: string } | undefined;
              if (existingUnit) {
                await api.updateUnit(existingUnit.id, {
                  unitNumber: unit.unitNumber,
                  m2: unit.m2,
                  bedrooms: unit.bedrooms,
                  bathrooms: unit.bathrooms,
                  status: unit.status,
                  notes: unit.notes,
                });
              } else {
                await api.createUnit({
                  propertyId: id,
                  unitNumber: unit.unitNumber,
                  m2: unit.m2,
                  bedrooms: unit.bedrooms,
                  bathrooms: unit.bathrooms,
                  status: unit.status,
                  notes: unit.notes,
                });
              }
            }
          }

          const formUnitNumbers = new Set((units || []).map((u: any) => u.unitNumber));
          for (const u of existingUnitsList) {
            if (!formUnitNumbers.has(u.unitNumber)) {
              await api.deleteUnit(u.id);
            }
          }

          const updatedRaw = await api.getUnits(id);
          const updatedList: any[] = Array.isArray(updatedRaw) ? updatedRaw : (updatedRaw as any)?.data ?? [];
          const transformedUnits = updatedList.map((unit: any) => ({
            id: unit.id,
            propertyId: unit.propertyId,
            unitNumber: unit.unitNumber,
            m2: typeof unit.m2 === 'number' ? unit.m2 : parseFloat(unit.m2) || 0,
            bedrooms: unit.bedrooms,
            bathrooms: unit.bathrooms,
            status: unit.status || 'available',
            tenantId: unit.tenantId,
            tenantName: unit.tenant?.name || unit.tenantName,
            monthlyRent: unit.monthlyRent ? (typeof unit.monthlyRent === 'number' ? unit.monthlyRent : parseFloat(unit.monthlyRent)) : undefined,
            contractStartDate: unit.contractStartDate,
            contractEndDate: unit.contractEndDate,
            notes: unit.notes,
          }));
          setUnits(transformedUnits);
        } catch (unitsErr: any) {
          console.error('Error saving units:', unitsErr);
          setError('Propiedad actualizada, pero hubo un error al guardar algunas unidades');
        }
      }

      await queryClient.invalidateQueries({ queryKey: ['property', id] });
      await queryClient.invalidateQueries({ queryKey: ['property-timeline', id] });
      await queryClient.invalidateQueries({ queryKey: ['change-logs', 'PROPERTY', id] });

      handleCloseEditModal();
    } catch (err: any) {
      console.error('Error saving property:', err);
      setError(err.message || 'Error al guardar la propiedad');
    } finally {
      setIsPropertyFormSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/propiedades')}>
            Volver
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">{property.name}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" icon={Download} onClick={handleDownloadBackOfficeReport}>
            Descargar reporte
          </Button>
          {canUpdateProperties && (
            <Button variant="secondary" icon={Edit} onClick={handleOpenEditModal}>
              Editar
            </Button>
          )}
        </div>
      </div>

      {/* Tabs (Unidades e Inversionistas solo si el rol tiene permiso) */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {([
            { id: 'general' as const, label: 'General' },
            ...(canViewUnits ? [{ id: 'units' as const, label: 'Unidades' }] : []),
            { id: 'documents' as const, label: 'Documentos' },
            { id: 'history' as const, label: 'Historial' },
            ...(canManageAccountStatements
              ? [{ id: 'statements' as const, label: 'Estado de Cuenta' }]
              : []),
            ...(canViewInvestors ? [{ id: 'investors' as const, label: 'Inversionistas' }] : []),
            { id: 'tenants' as const, label: 'Inquilinos' },
          ] as const).map((tab) => (
            <button
              type="button"
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-accent text-accent'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          {/* Imagen de Portada */}
          {property.coverImage && (
            <Card title="Imagen de Portada">
              <div className="relative w-full h-64 md:h-96 rounded-lg overflow-hidden border border-gray-200">
                <img
                  src={getPropertyCoverImageUrl(property.coverImage)}
                  alt={property.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = PROPERTY_IMAGE_PLACEHOLDER;
                  }}
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Para cambiar la imagen, edita la propiedad desde la lista
              </p>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card title="Información General">
              <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">Dirección</label>
                <p className="text-base font-medium">{property.address}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Ciudad</label>
                <p className="text-base font-medium">{property.city}</p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Unidades</label>
                <p className="text-base font-medium">
                  {property.occupiedUnits} ocupadas de {property.unitCount} totales
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Metros Cuadrados</label>
                <p className="text-base font-medium">{property.m2Total} m²</p>
              </div>
              <div>
                <label className="text-sm text-gray-600">Precio por m²</label>
                <p className="text-base font-medium">
                  {canViewPrices ? Number(property.pricePerM2).toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 2 }) : '—'}
                </p>
              </div>
              {canViewPrices && (
                <>
                  <div>
                    <label className="text-sm text-gray-600">Valor actual</label>
                    <p className="text-2xl font-bold text-primary">
                      {property.currentValue != null && Number(property.currentValue) > 0
                        ? formatPropertyMoney(property.currentValue)
                        : '—'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Plusvalía</label>
                    <p
                      className={`text-xl font-semibold ${
                        (() => {
                          const pct = computePlusvaliaPct(
                            property.totalValue,
                            property.currentValue,
                          );
                          if (pct == null) return 'text-gray-500';
                          if (pct > 0) return 'text-green-700';
                          if (pct < 0) return 'text-red-600';
                          return 'text-gray-700';
                        })()
                      }`}
                    >
                      {formatPlusvaliaPct(
                        computePlusvaliaPct(property.totalValue, property.currentValue),
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Costo total</label>
                    <p className="text-lg font-medium text-gray-900">
                      {formatPropertyMoney(property.totalValue)}
                    </p>
                  </div>
                </>
              )}
              <div>
                <label className="text-sm text-gray-600">Estado</label>
                <p className="text-base font-medium">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      property.status === 'en-proyecto'
                        ? 'bg-yellow-100 text-yellow-800'
                        : property.status === 'en-construccion'
                        ? 'bg-orange-100 text-orange-800'
                        : property.status === 'en-remodelacion'
                        ? 'bg-purple-100 text-purple-800'
                        : property.status === 'operativo'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {property.status === 'en-proyecto'
                      ? 'En Proyecto'
                      : property.status === 'en-construccion'
                      ? 'En Construcción'
                      : property.status === 'en-remodelacion'
                      ? 'En Remodelación'
                      : property.status === 'operativo'
                      ? 'Operativo'
                      : property.status}
                  </span>
                </p>
              </div>
            </div>
          </Card>

          <Card title="Métricas">
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">Ocupación</label>
                <div className="mt-2">
                  <div className="flex justify-between text-sm mb-1">
                    <span>
                      {property.unitCount > 0 ? Math.round((property.occupiedUnits / property.unitCount) * 100) : 0}%
                    </span>
                    <span>{property.occupiedUnits}/{property.unitCount} unidades</span>
                  </div>
                  <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent"
                      style={{
                        width: `${property.unitCount > 0 ? (property.occupiedUnits / property.unitCount) * 100 : 0}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Card>
          </div>
        </div>
      )}

      {activeTab === 'investors' && (
        <div>
          <div className="mb-4">
            <h2 className="text-lg font-semibold">Inversionistas</h2>
            <p className="text-sm text-gray-500 mt-1">
              Información de los inversionistas que participan en esta propiedad
            </p>
          </div>

          {/* Ownership Validation Alert */}
          {ownershipTotal > 100 && (
            <div className="mb-4 p-4 bg-accent/10 border border-accent rounded-lg">
              <p className="text-accent font-medium">
                ⚠️ La suma de participación excede 100%. Actual: {ownershipTotal.toFixed(1)}%
              </p>
            </div>
          )}

          {/* Ownership Progress Bar */}
          <Card className="mb-4">
            <div className="mb-2">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Participación Distribuida</span>
                <span className="font-medium">{ownershipTotal.toFixed(1)}%</span>
              </div>
              <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full ${
                    ownershipTotal > 100 ? 'bg-red-500' : ownershipTotal === 100 ? 'bg-green-500' : 'bg-accent'
                  }`}
                  style={{ width: `${Math.min(ownershipTotal, 100)}%` }}
                />
              </div>
            </div>
          </Card>

          <Card>
            <Table data={propertyInvestments} columns={investmentColumns} />
          </Card>
        </div>
      )}

      {activeTab === 'tenants' && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Inquilinos</h2>
          <Card>
            <Table data={propertyTenants} columns={tenantColumns} />
          </Card>
        </div>
      )}

      {activeTab === 'units' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Unidades del Edificio</h2>
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span>
                Total: <span className="font-semibold text-primary">{propertyUnits.length}</span>
              </span>
              <span>
                Ocupadas: <span className="font-semibold text-green-600">
                  {propertyUnits.filter(u => u.status === 'occupied').length}
                </span>
              </span>
              <span>
                Disponibles: <span className="font-semibold text-blue-600">
                  {propertyUnits.filter(u => u.status === 'available').length}
                </span>
              </span>
            </div>
          </div>
          <Card>
            <Table data={propertyUnits} columns={unitColumns} />
          </Card>
        </div>
      )}

      {activeTab === 'documents' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-semibold">Documentos</h2>
              <p className="text-sm text-gray-500 mt-1">
                Documentos adjuntos a esta propiedad
              </p>
            </div>
          </div>
          <Card>
            {property.documents && property.documents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {property.documents
                  .filter((d) => !!d.fileUrl)
                  .map((document) => (
                    <div
                      key={document.id}
                      className="relative rounded-lg border border-gray-200 bg-white hover:border-primary/30 hover:shadow-sm transition-all"
                    >
                      {canDeleteProperties && (
                        <button
                          type="button"
                          onClick={() =>
                            setPendingDeleteDocument({
                              id: document.id,
                              title: document.title,
                            })
                          }
                          className="absolute top-2 right-2 z-10 p-2 rounded-lg text-red-600 bg-white/95 border border-gray-100 shadow-sm hover:bg-red-50 transition-colors"
                          title="Eliminar documento"
                          aria-label="Eliminar documento"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      <DocumentViewer
                        title={document.title}
                        fileUrl={document.fileUrl!}
                        fileTypeLabel={`${(document.fileType?.toUpperCase() || 'DOC')} · ${formatDate(document.uploadedAt)}`}
                        downloadName={
                          document.title
                            ? `${document.title}.${document.fileType || 'pdf'}`
                            : undefined
                        }
                        className="p-4 border-0 rounded-lg shadow-none hover:shadow-none"
                      />
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No hay documentos adjuntos a esta propiedad</p>
              </div>
            )}
          </Card>
        </div>
      )}

      {activeTab === 'statements' && (
        <div className="space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Estado de Cuenta (Propiedad)</h2>
              <p className="text-sm text-gray-500 mt-1">
                Registra 1 estado de cuenta por mes para esta propiedad. Se distribuye automáticamente a los inversionistas según su %.
              </p>
            </div>
            <Button variant="secondary" icon={Pencil} onClick={openCreateStatementModal}>
              Registrar estado de cuenta
            </Button>
          </div>

          <Card title="Historial">
            {propertyStatementsLoading ? (
              <div className="text-sm text-gray-500 py-6 text-center">Cargando...</div>
            ) : propertyStatements.length === 0 ? (
              <div className="text-sm text-gray-500 py-6 text-center">Sin registros.</div>
            ) : (
              <div className="space-y-3">
                {propertyStatements.map((s: any) => (
                  <div key={s.id} className="rounded-lg border border-gray-200 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {s.period || `${String(s.month).padStart(2, '0')}/${s.year}`}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Ingreso: ${Number(s.income).toLocaleString('es-MX')} · Egreso:{' '}
                          ${Number(s.expenses).toLocaleString('es-MX')} · Neto:{' '}
                          ${Number(s.netIncome).toLocaleString('es-MX')} · Utilidad total:{' '}
                          ${Number(s.propertyProfit).toLocaleString('es-MX')}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 justify-end">
                        {s.fileUrl ? (
                          <a
                            className="text-sm font-semibold text-primary hover:underline"
                            href={getPropertyDocumentDownloadUrl(s.fileUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Descargar
                          </a>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => openEditStatementModal(s)}
                          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-primary hover:bg-primary/10"
                        >
                          <Pencil className="h-4 w-4" />
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setPendingDeleteStatement({
                              id: s.id,
                              periodLabel: s.period || `${String(s.month).padStart(2, '0')}/${s.year}`,
                            })
                          }
                          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-medium text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {activeTab === 'history' && property && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Historial de Cambios</h2>
          <HistoryTable rows={historyRows} loading={historyLoading} />
        </div>
      )}

      {/* Modal para Editar Propiedad */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        title="Editar Propiedad"
        size="xl"
      >
        <PropertyForm
          property={property}
          propertyUnits={units}
          onSubmit={handleEditSubmit}
          onCancel={handleCloseEditModal}
          isSubmitting={isPropertyFormSubmitting}
        />
      </Modal>

      <Modal
        isOpen={statementModalOpen}
        onClose={closeStatementModal}
        title={
          propertyStatementEdit
            ? 'Editar estado de cuenta de la propiedad'
            : 'Registrar estado de cuenta de la propiedad'
        }
        size="lg"
      >
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!id) return;
            setStatementError(null);
            setStatementSubmitting(true);
            try {
              const income = parseFloat(statementForm.income.replace(',', '.')) || 0;
              const expenses = parseFloat(statementForm.expenses.replace(',', '.')) || 0;
              const netIncome = parseFloat(statementForm.netIncome.replace(',', '.'));
              const propertyProfit = parseFloat(statementForm.propertyProfit.replace(',', '.'));

              let fileUrl: string | undefined;
              if (statementFile) {
                const res = await api.uploadAccountStatementFile(statementFile);
                fileUrl = (res as any)?.fileUrls?.[0];
              }

              await api.createPropertyAccountStatement({
                propertyId: id,
                month: statementForm.month,
                year: statementForm.year,
                income,
                expenses,
                ...(Number.isFinite(netIncome) ? { netIncome } : {}),
                ...(Number.isFinite(propertyProfit) ? { propertyProfit } : {}),
                ...(fileUrl !== undefined ? { fileUrl } : {}),
              });

              await queryClient.invalidateQueries({ queryKey: ['property-account-statements', id] });
              await queryClient.invalidateQueries({ queryKey: ['account-statements'] });
              closeStatementModal();
              resetStatementFormDefaults();
            } catch (err: any) {
              setStatementError(err?.message || 'Error al guardar el estado de cuenta');
            } finally {
              setStatementSubmitting(false);
            }
          }}
        >
          {statementError && (
            <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">
              {statementError}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mes</label>
              <select
                value={statementForm.month}
                disabled={!!propertyStatementEdit}
                onChange={(e) =>
                  setStatementForm((p) => ({ ...p, month: Number(e.target.value) }))
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50 disabled:cursor-not-allowed"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Año</label>
              <input
                type="number"
                value={statementForm.year}
                disabled={!!propertyStatementEdit}
                onChange={(e) =>
                  setStatementForm((p) => ({ ...p, year: Number(e.target.value) }))
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ingreso</label>
              <input
                value={statementForm.income}
                onChange={(e) => setStatementForm((p) => ({ ...p, income: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Egreso</label>
              <input
                value={statementForm.expenses}
                onChange={(e) =>
                  setStatementForm((p) => ({ ...p, expenses: e.target.value }))
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                placeholder="0"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ingreso Neto (opcional)
              </label>
              <input
                value={statementForm.netIncome}
                onChange={(e) =>
                  setStatementForm((p) => ({ ...p, netIncome: e.target.value }))
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                placeholder="Si se omite: ingreso - egreso"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Utilidad total (opcional)
              </label>
              <input
                value={statementForm.propertyProfit}
                onChange={(e) =>
                  setStatementForm((p) => ({ ...p, propertyProfit: e.target.value }))
                }
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                placeholder="Si se omite: ingreso neto"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Archivo (PDF/Excel opcional)
            </label>
            <input
              type="file"
              accept=".pdf,.xlsx,.xls,.csv,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
              onChange={(e) => handleStatementFileChange(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-white hover:file:bg-accent/90"
            />
            {propertyStatementEdit?.fileUrl && !statementFile && (
              <p className="mt-1 text-xs text-gray-500">
                Hay un archivo adjunto. Sube uno nuevo solo si deseas reemplazarlo.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={closeStatementModal}
              disabled={statementSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={statementSubmitting}>
              {statementSubmitting ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal para Editar Unidad / Asignar inquilino */}
      <Modal
        isOpen={!!editingUnit}
        onClose={() => { setEditingUnit(null); }}
        title={editingUnit ? `Unidad ${editingUnit.unitNumber} – Inquilino, renta y contrato` : ''}
        size="md"
      >
        {editingUnit && (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!editingUnit) return;
              setUnitSaveLoading(true);
              try {
                const selectedTenant = unitForm.tenantId ? propertyTenants.find((t) => t.id === unitForm.tenantId) : null;
                await api.updateUnit(editingUnit.id, {
                  tenantId: unitForm.tenantId || null,
                  monthlyRent: unitForm.monthlyRent ? Number(unitForm.monthlyRent) : null,
                  contractStartDate: unitForm.contractStartDate || null,
                  contractEndDate: unitForm.contractEndDate || null,
                  status: unitForm.status,
                });
                await reloadUnitsAndTenants();
                setEditingUnit(null);
              } catch (err: any) {
                console.error('Error updating unit:', err);
                setError(err.message || 'Error al guardar la unidad');
              } finally {
                setUnitSaveLoading(false);
              }
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Inquilino</label>
              <select
                value={unitForm.tenantId}
                onChange={(e) => setUnitForm((f) => ({ ...f, tenantId: e.target.value }))}
                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Sin inquilino</option>
                {propertyTenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} – Unidad {t.unitNumber}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Renta mensual</label>
              <input
                type="number"
                min={0}
                step={100}
                value={unitForm.monthlyRent}
                onChange={(e) => setUnitForm((f) => ({ ...f, monthlyRent: e.target.value }))}
                placeholder="Ej: 15000"
                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Inicio contrato</label>
                <input
                  type="date"
                  value={unitForm.contractStartDate}
                  onChange={(e) => setUnitForm((f) => ({ ...f, contractStartDate: e.target.value }))}
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fin contrato</label>
                <input
                  type="date"
                  value={unitForm.contractEndDate}
                  onChange={(e) => setUnitForm((f) => ({ ...f, contractEndDate: e.target.value }))}
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                value={unitForm.status}
                onChange={(e) => setUnitForm((f) => ({ ...f, status: e.target.value as Unit['status'] }))}
                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="available">Disponible</option>
                <option value="occupied">Ocupada</option>
                <option value="maintenance">Mantenimiento</option>
                <option value="reserved">Reservada</option>
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setEditingUnit(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={unitSaveLoading}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50"
              >
                {unitSaveLoading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDeleteModal
        isOpen={pendingDeleteDocument !== null}
        onClose={() =>
          !deletePropertyDocumentMutation.isPending && setPendingDeleteDocument(null)
        }
        onConfirm={() => {
          if (!pendingDeleteDocument) return;
          deletePropertyDocumentMutation.mutate(pendingDeleteDocument.id);
        }}
        title="Eliminar documento"
        message={
          pendingDeleteDocument ? (
            <>
              ¿Eliminar el documento <strong>{pendingDeleteDocument.title}</strong> de esta
              propiedad?
            </>
          ) : null
        }
        isLoading={deletePropertyDocumentMutation.isPending}
      />

      <ConfirmDeleteModal
        isOpen={pendingDeleteStatement !== null}
        onClose={() =>
          !deletePropertyStatementMutation.isPending && setPendingDeleteStatement(null)
        }
        onConfirm={() => {
          if (!pendingDeleteStatement) return;
          deletePropertyStatementMutation.mutate(pendingDeleteStatement.id);
        }}
        title="Eliminar estado de cuenta"
        message={
          pendingDeleteStatement ? (
            <>
              ¿Eliminar el estado de cuenta de{' '}
              <strong>{pendingDeleteStatement.periodLabel}</strong>? También se eliminará la
              distribución mostrada a los inversionistas para ese mes.
            </>
          ) : null
        }
        isLoading={deletePropertyStatementMutation.isPending}
      />
    </div>
  );
};
